/**
 * 智能体用户素材库管理器
 * 负责管理智能体用户素材库的业务操作
 * @author Joey
 */

import { useToast } from "@/hooks/use-toast";
import { UserMaterialFolderData, userMaterialListFolders } from "@/services/user-material-list-folders";
import { canUseWindow } from "@/utils/canuseWindow";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { userMaterialAddAgentUserMaterialFolder } from "@/services/user-material-add-agent-user-material-folders";
import { PUSH_SETTINGS_TYPE, userManagementManager } from "./agent-dashboard-user-management-manager";
import { AgentUserMaterialFolderData, getAgentUserMaterialFolders } from "@/services/get-agent-user-material-folders";
import { deleteAgentUserMaterialFolder } from "@/services/delete-agent-user-material-folder";
import { MaterialFolderItemData, userMaterialGetFolderItems } from "@/services/user-material-get-folder-items";

interface MaterialFolderSearchForm {
  page: number;
  pageSize: number;
  total: number;
}

type MaterialFoldersChangeEventDetail = {
  materialFolders: UserMaterialFolderData[];
}
type MaterialFoldersChangeEvent = CustomEvent<MaterialFoldersChangeEventDetail>;

type MaterialFolderSearchFormChangeEventDetail = {
  materialFolderSearchForm: MaterialFolderSearchForm;
}
type MaterialFolderSearchFormChangeEvent = CustomEvent<MaterialFolderSearchFormChangeEventDetail>;

type AgentUserMaterialsChangeEventDetail = {
  agentUserMaterials: AgentUserMaterialFolderData[];
}
type AgentUserMaterialsChangeEvent = CustomEvent<AgentUserMaterialsChangeEventDetail>;

type MaterialFolderItemsChangeEventDetail = {
  materialFolderItems: MaterialFolderItemData[];
}
type MaterialFolderItemsChangeEvent = CustomEvent<MaterialFolderItemsChangeEventDetail>;

export class AgentDashboardMaterialManager {
  private static instance: AgentDashboardMaterialManager;
  private readonly STORAGE_KEY = "MIUTA_AGENT_DASHBOARD_MATERIAL";
  private agentId: string = '';
  private userId: string = '';
  // 智能体用户素材库列表
  private agentUserMaterials: AgentUserMaterialFolderData[] = [];
  // 素材库目录列表
  private materialFolders: UserMaterialFolderData[] = [];
  // 素材库目录请求表单
  private materialFolderSearchForm: MaterialFolderSearchForm = {
    page: 1,
    pageSize: 1000,
    total: 0,
  }
  // 素材库文件夹内文件列表
  private materialFolderItems: MaterialFolderItemData[] = [];

  private constructor() { }

  // 单例模式
  public static getInstance(): AgentDashboardMaterialManager {
    if (!AgentDashboardMaterialManager.instance) {
      AgentDashboardMaterialManager.instance = new AgentDashboardMaterialManager();
    }
    return AgentDashboardMaterialManager.instance;
  }

  // 初始化
  public async init({ agentId, userId }: { agentId: string, userId: string }) {
    this.setAgentId(agentId);
    this.setUserId(userId);
    try {
      // 初始化时，如果列表为空，才请求数据
      // if (this.getAgentUserMaterials().length === 0) {
      //   await this.fetchAgentUserMaterials({ agentId, userId });
      // }
    } catch (error) {
      console.error("Failed to init agent material folder list cache:", error);
      throw error;
    }
  }

  // 请求素材库目录数据（用于添加素材库模板列表）
  public async fetchMaterialFolders(page?: number, userId?: string) {
    try {
      const data = await userMaterialListFolders({
        page: page || this.materialFolderSearchForm.page,
        pageSize: this.materialFolderSearchForm.pageSize,
        userId: userId || "",
        agentId: this.getAgentId(),
      });

      this.setMaterialFolders(data.list);

    } catch (error) {
      console.error("Failed to fetch assets material list:", error);
      this.dispatchMaterialFoldersChange();
      throw error;
    }
  }

  // 请求智能体用户素材库
  public async fetchAgentUserMaterials({
    agentId,
    userId,
    type,
  }: { agentId?: string, userId?: string, type?: number }) {
    try {
      const data = await getAgentUserMaterialFolders({
        agentId: agentId || this.getAgentId(),
        page: 1,
        pageSize: 1000,
        userId: userId || this.getUserId(),
        type: type || PUSH_SETTINGS_TYPE[userManagementManager.getActiveSettingTab()],
      });
      this.setAgentUserMaterials(data.list || []);
    } catch (error) {
      console.error("Failed to fetch agent user materials list:", error);
      this.dispatchAgentUserMaterialsChange();
      throw error;
    }
  }

  // 请求智能体用户素材库指定文件夹内素材数据
  public async fetchMaterialFolderItems(folderId: number) {
    try {
      const data = await userMaterialGetFolderItems(folderId);
      this.setMaterialFolderItems(data.items || []);
    } catch (error) {
      console.error("Failed to fetch folder items list:", error);
      this.dispatchMaterialFolderItemsChange();
      throw error;
    }
  }

  // 创建智能体用户素材库
  public async agentUserMaterialCreate({
    type,
    folderId
  }: { type: number, folderId: number[] }) {
    try {
      const res = await userMaterialAddAgentUserMaterialFolder({
        agentId: this.getAgentId(),
        userId: this.getUserId(),
        folderId,
        type,
      });
      return res;
    } catch (error) {
      console.error("Failed to create assets material folder:", error);
      this.dispatchMaterialFoldersChange();
      throw error;
    }
  }

  // 删除智能体用户素材
  public async agentUserMaterialDelete(id: number) {
    try {
      // 请求删除素材目录
      await deleteAgentUserMaterialFolder({
        agentId: this.getAgentId(),
        userId: this.getUserId(),
        type: PUSH_SETTINGS_TYPE[userManagementManager.getActiveSettingTab()],
        folderId: id,
      });
    } catch (error) {
      // 回退更新
      console.log(error)
      // 继续抛出异常
      throw error;
    }
  }

  // ==================== 读取写入操作 ====================
  public setAgentId(agentId: string) {
    this.agentId = agentId;
  }
  public getAgentId() {
    return this.agentId;
  }

  public setUserId(userId: string) {
    this.userId = userId;
  }
  public getUserId() {
    return this.userId;
  }

  public getMaterialFolders() {
    return this.materialFolders;
  }
  public setMaterialFolders(list: UserMaterialFolderData[]) {
    this.materialFolders = list;
    this.dispatchMaterialFoldersChange();
    // // 缓存数据到本地 只缓存 40 条数据
    // this.saveToLocal({
    //   materialFolders: this.materialFolders.slice(0, 40)
    // });
  }

  public setMaterialFolderSearchForm(form: MaterialFolderSearchForm) {
    this.materialFolderSearchForm = form;
    this.dispatchMaterialFolderSearchFormChange();
  }
  public getMaterialFolderSearchForm() {
    return this.materialFolderSearchForm;
  }

  public getAgentUserMaterials() {
    return this.agentUserMaterials;
  }
  public setAgentUserMaterials(list: AgentUserMaterialFolderData[]) {
    this.agentUserMaterials = list;
    this.dispatchAgentUserMaterialsChange();
  }

  public getMaterialFolderItems() {
    return this.materialFolderItems;
  }
  public setMaterialFolderItems(list: MaterialFolderItemData[]) {
    this.materialFolderItems = list;
    this.dispatchMaterialFolderItemsChange();
  }

  // ==================== 订阅通知操作 ====================
  // 监听目录列表变化事件
  private dispatchMaterialFoldersChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<MaterialFoldersChangeEventDetail>(`${this.STORAGE_KEY}:material-folders-change`, {
      detail: { materialFolders: this.materialFolders }
    });
    window.dispatchEvent(event);
  }
  public onMaterialFoldersChange(listener: (e: MaterialFoldersChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:material-folders-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听请求表单变化事件
  private dispatchMaterialFolderSearchFormChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<MaterialFolderSearchFormChangeEventDetail>(`${this.STORAGE_KEY}:material-form-change`, {
      detail: { materialFolderSearchForm: this.materialFolderSearchForm }
    });
    window.dispatchEvent(event);
  }
  public onMaterialFolderSearchFormChange(listener: (e: MaterialFolderSearchFormChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:material-form-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听用户素材库目录变化事件
  private dispatchAgentUserMaterialsChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<AgentUserMaterialsChangeEventDetail>(`${this.STORAGE_KEY}:agent-user-materials-change`, {
      detail: { agentUserMaterials: this.agentUserMaterials }
    });
    window.dispatchEvent(event);
  }
  public onAgentUserMaterialsChange(listener: (e: AgentUserMaterialsChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:agent-user-materials-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }

  // 监听素材库文件夹内文件列表变化事件
  private dispatchMaterialFolderItemsChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<MaterialFolderItemsChangeEventDetail>(`${this.STORAGE_KEY}:material-folder-items-change`, {
      detail: { materialFolderItems: this.materialFolderItems }
    });
    window.dispatchEvent(event);
  }
  public onMaterialFolderItemsChange(listener: (e: MaterialFolderItemsChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:material-folder-items-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }
}

// =============================== Hooks ===============================
// 请求素材库目录数据（用于添加素材库模板列表）
export const useMaterialFoldersData = () => {
  const toast = useToast();
  const userId = new URLSearchParams(window.location.search).get("userId");

  const [materialFolders, setMaterialFolders] = useState(agentDashboardMaterialManager.getMaterialFolders());
  const [loading, setLoading] = useState(false);

  const init = async () => {
    try {
      setLoading(true);
      await agentDashboardMaterialManager.fetchMaterialFolders(undefined, userId);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const unsubscribe = agentDashboardMaterialManager.onMaterialFoldersChange((e) => {
      setMaterialFolders(e.detail.materialFolders)
    })
    return unsubscribe
  }, [])

  return {
    materialFolders,
    loading,
  }
}

// 请求智能体用户素材库
export const useAgentUserMaterialData = () => {
  const toast = useToast();
  const params = useParams();
  const _userId = new URLSearchParams(window.location.search).get("userId");

  const [agentUserMaterials, setAgentUserMaterials] = useState(agentDashboardMaterialManager.getAgentUserMaterials());
  const [loading, setLoading] = useState(false);

  const init = async () => {
    try {
      setLoading(true);
      await agentDashboardMaterialManager.init({
        agentId: params.id,
        userId: _userId,
      });
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const searchAgentUserMaterials = async () => {
    try {
      setLoading(true);
      await agentDashboardMaterialManager.fetchAgentUserMaterials({
        agentId: params.id,
        userId: _userId,
      });
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const unsubscribe = agentDashboardMaterialManager.onAgentUserMaterialsChange((e) => {
      setAgentUserMaterials(e.detail.agentUserMaterials)
    })
    return unsubscribe
  }, [])

  return {
    agentUserMaterials,
    searchAgentUserMaterials,
    loading,
  }
}

// 请求智能体用户素材库指定文件夹内素材数据
export const useAgentUserMaterialItems = () => {
  const toast = useToast();

  const [materialFolderItems, setMaterialFolderItems] = useState<MaterialFolderItemData[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchMaterialFolderItems = async (folderId: number) => {
    try {
      setLoading(true);
      await agentDashboardMaterialManager.fetchMaterialFolderItems(folderId);
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const unsubscribe = agentDashboardMaterialManager.onMaterialFolderItemsChange((e) => {
      setMaterialFolderItems(e.detail.materialFolderItems)
    })
    return unsubscribe
  }, [])

  return {
    materialFolderItems,
    loading,
    fetchMaterialFolderItems,
  }
}

// 创建智能体用户素材库
export const useAgentUserMaterialCreate = () => {
  const toast = useToast();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [agentUserMaterials, setAgentUserMaterials] = useState(agentDashboardMaterialManager.getAgentUserMaterials());

  const agentUserMaterialCreate = async (folderId: number[]) => {
    try {
      setLoading(true);

      // 根据 folderId，判断智能体用户素材库列表中已存在的项
      const validIds = folderId
        .filter(id => !agentDashboardMaterialManager.getAgentUserMaterials()
          .some(item => item.folderId === id));

      if (validIds.length === 0) {
        toast.toast({
          description: t('agent_dashboard.user_detail.material.add_modal.save_valid_tips')
        });
        return;
      }

      // 只允许添加一个文件夹
      if (agentUserMaterials.length >= 1) {
        toast.toast({
          description: t('agent_creation.subscription_settings.add_remove_modal.disabled_tips'),
        });
        return false;
      }

      const params = {
        folderId,
        type: PUSH_SETTINGS_TYPE[userManagementManager.getActiveSettingTab()],
      }
      await agentDashboardMaterialManager.agentUserMaterialCreate(params);
      // 刷新智能体用户素材库列表
      await agentDashboardMaterialManager.fetchAgentUserMaterials({});
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const unsubscribe = agentDashboardMaterialManager.onAgentUserMaterialsChange((e) => {
      setAgentUserMaterials(e.detail.agentUserMaterials)
    })
    return unsubscribe
  }, [])

  return {
    agentUserMaterialCreate,
    loading,
  }
}

// 删除智能体用户素材库
export const useAgentUserMaterialDelete = () => {
  const { showToastByCode } = useToast();
  const [loading, setLoading] = useState(false);

  const agentUserMaterialDelete = async (id: number) => {
    try {
      setLoading(true);
      await agentDashboardMaterialManager.agentUserMaterialDelete(id);
      // 刷新智能体用户素材库列表
      await agentDashboardMaterialManager.fetchAgentUserMaterials({});
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  return {
    agentUserMaterialDelete,
    loading,
  };
};

export const agentDashboardMaterialHooks = {
  // 素材库添加弹窗列表数据
  useMaterialFoldersData,
  // 智能体用户素材库数据
  useAgentUserMaterialData,
  useAgentUserMaterialItems,
  useAgentUserMaterialCreate,
  useAgentUserMaterialDelete,
}

// 导出单例实例
export const agentDashboardMaterialManager = AgentDashboardMaterialManager.getInstance();
